/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import OAuthHandler from 'ads-account-connection/components/OAuthHandler';
import OpenOAuthWindow from './OpenOAuthWindow';
import AdAccountConnectModalContainer from './AdAccountConnectModalContainer';
import Networks from 'ads-account-connection/lib/Networks';
import AccountConnectionData from 'ads-account-connection/data/AccountConnectionData';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { getHubSpotUserId } from 'ads-account-connection/lib/AuthSelectors';
import { getHubs2Info } from 'ads-account-connection/data/HubData';
const AccountConnectState = {
  LOADING: 'LOADING',
  OAUTH: 'OAUTH',
  INITIAL_ACCOUNT_CONNECT: 'INITIAL_ACCOUNT_CONNECT',
  USER_ALREADY_CONNECTED: 'USER_ALREADY_CONNECTED'
};
const HUB_TYPES = {
  DEVELOPER_TEST: 8
};

const AccountConnectContainer = ({
  onFailure: failureHandler,
  network,
  auth,
  maxSelectableAccounts,
  onClickCreateAdAccount,
  allowDeselectInModals,
  onAccountConnected,
  onComplete,
  onNetworkConnected,
  onOAuthFailure,
  showMissingPermissionsWarning = false,
  forceReauth = false,
  trackEvent,
  adwordsSelectedFromOnboarding = false,
  isDeveloperPortal: isDeveloperPortalProps = null
}) => {
  const [currentState, setCurrentState] = useState(AccountConnectState.LOADING);
  const [networkUserId, setNetworkUserId] = useState('');
  const hubSpotUserId = getHubSpotUserId(auth);
  const [isDeveloperPortal, setIsDeveloperPortal] = useState(isDeveloperPortalProps);
  const shouldShowAuthModal = useRef(forceReauth);
  useEffect(() => {
    if (isDeveloperPortalProps === null) {
      getHubs2Info().then(response => {
        setIsDeveloperPortal(response.hubType === HUB_TYPES.DEVELOPER_TEST);
      });
    }
  }, [isDeveloperPortalProps]);
  const onFailure = useCallback(err => {
    if (failureHandler) {
      failureHandler(err);
    }

    onComplete();
  }, [failureHandler, onComplete]);
  useEffect(() => {
    if (shouldShowAuthModal.current) {
      shouldShowAuthModal.current = false;
      setCurrentState(AccountConnectState.OAUTH);
      OpenOAuthWindow(network);
      return;
    }

    AccountConnectionData.fetchConnectedAuths().then(connectedAuths => {
      const alreadyConnectedAuth = connectedAuths.find(user => user.hubSpotUserId === hubSpotUserId && user.network === network);

      if (alreadyConnectedAuth) {
        setNetworkUserId(alreadyConnectedAuth.networkUserId);
        setCurrentState(AccountConnectState.USER_ALREADY_CONNECTED);
      } else {
        setCurrentState(AccountConnectState.OAUTH);
        OpenOAuthWindow(network);
      }
    }).catch(err => {
      onFailure(err);
    });
  }, [network, hubSpotUserId, onFailure]);
  const oAuthFailure = useCallback(data => {
    if (data.network !== network) {
      return;
    }

    if (data.network === Networks.LINKEDIN && data.error === 'user_cancelled_login') {
      // Not an error if they just canceled.
      onComplete();
      return;
    }

    if (onOAuthFailure) {
      onOAuthFailure(data);
    }

    onComplete();
  }, [onOAuthFailure, network, onComplete]);
  const oAuthSuccess = useCallback(data => {
    if (data.network !== network) {
      return;
    }

    const {
      networkUserId: netUserId
    } = data;
    setNetworkUserId(netUserId);
    AccountConnectionData.generateOAuthorKey(network, netUserId).then(() => {
      if (onNetworkConnected) {
        onNetworkConnected(network);
      }
    }).then(() => {
      setCurrentState(AccountConnectState.INITIAL_ACCOUNT_CONNECT);
    }).catch(err => {
      onFailure(err);
    });
  }, [network, onNetworkConnected, onFailure]);

  switch (currentState) {
    case AccountConnectState.INITIAL_ACCOUNT_CONNECT:
    case AccountConnectState.USER_ALREADY_CONNECTED:
      {
        return /*#__PURE__*/_jsx(AdAccountConnectModalContainer, {
          onComplete: onComplete,
          onFailure: onFailure,
          network: network,
          auth: auth,
          maxSelectableAccounts: maxSelectableAccounts,
          onClickCreateAdAccount: onClickCreateAdAccount,
          allowDeselectInModals: allowDeselectInModals,
          onAccountConnected: onAccountConnected,
          showMissingPermissionsWarning: showMissingPermissionsWarning,
          trackEvent: trackEvent,
          networkUsersId: networkUserId,
          adwordsSelectedFromOnboarding: adwordsSelectedFromOnboarding,
          isDeveloperPortal: isDeveloperPortal
        });
      }

    case AccountConnectState.LOADING:
      {
        return /*#__PURE__*/_jsx(UILoadingOverlay, {});
      }

    case AccountConnectState.OAUTH:
      {
        return /*#__PURE__*/_jsx(OAuthHandler, {
          onSuccess: oAuthSuccess,
          onFailure: oAuthFailure
        });
      }

    default:
      {
        return null;
      }
  }
};

AccountConnectContainer.propTypes = {
  network: PropTypes.oneOf(Object.values(Networks)).isRequired,
  maxSelectableAccounts: PropTypes.number.isRequired,
  onAccountConnected: PropTypes.func,
  onNetworkConnected: PropTypes.func,
  onOAuthFailure: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onFailure: PropTypes.func,
  onClickCreateAdAccount: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  allowDeselectInModals: PropTypes.bool.isRequired,
  showMissingPermissionsWarning: PropTypes.bool,
  forceReauth: PropTypes.bool,
  trackEvent: PropTypes.func,
  adwordsSelectedFromOnboarding: PropTypes.bool,
  isDeveloperPortal: PropTypes.bool
};
export default AccountConnectContainer;